import { memo, useCallback } from 'react';
import { Box } from '@mui/material';
import { flexRender, type Row } from '@tanstack/react-table';
import { useFormContext, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getUiDefinitionContainerStyles, getUiDefinitionTextStyles } from 'utils/styles';
import { shortenText } from 'utils/common';
import { Checkbox } from 'components/mui/common/Checkbox';
import type { DataSchema, Schema } from 'types';
import type { TableFormData } from 'components/common/Table/Table';
import { useTableManagement } from 'contexts/TableManagementContext';
import { getFormFieldName } from '../editableComponentFactory';
import { TableCellEdit } from '../TableCellEdit';
import { Button } from 'components/mui/common/Button';
import { TableRowControls } from '../TableRowControls';
import type { ReturnRow } from 'types/table';
import type { RowData } from 'utils/helios/row';
import { transformRowFieldData } from 'utils/helios/row';
import { TABLE_CELL_TYPE } from 'types/enums';
import { BUTTONS_GROUP_ATTRIBUTE_KEY } from 'utils/agendaRecord/record';
import {
  Card,
  CardContent,
  CardHeader,
  CardHeaderActions,
  CardRow,
  CellLabel,
  CellValueWrapper,
  EditButtons,
  EditLabelTypography,
  TextContainer,
  Wrapper,
} from './mobileTableStyles';

interface Props<T extends keyof P, P extends DataSchema> {
  agendaId: string;
  rows: Row<Schema<T, P>>[];
  dataSchema: P;
  selectAllForm: UseFormReturn<TableFormData<boolean>>;
  hideCheckbox?: boolean;
  selectedRows: string[];
  onRowClick?: (row: Schema<T, P>) => void;
  handleRowSelect: (identifier: string, isChecked: boolean) => void;
  onSaveEditedRow?: ReturnRow<P>;
  onCancelEditRow?: (id: string) => void;
}

type ColumnMeta<P extends DataSchema> = {
  uiDefinition: P;
};

const MobileFlowTableBase = <T extends keyof P, P extends DataSchema>({
  agendaId,
  rows,
  dataSchema,
  hideCheckbox,
  selectedRows,
  selectAllForm,
  handleRowSelect,
  onRowClick,
  onCancelEditRow,
  onSaveEditedRow,
}: Props<T, P>) => {
  const { t } = useTranslation();
  const { setValue: selectAllSetValue } = selectAllForm;
  const { control, handleSubmit: onSubmit, getValues } = useFormContext();
  const { shouldShowRowControls, isCellEditable, isRowNew, hasEditableActions, showSaveButton, showCancelEditButton } = useTableManagement();

  const handleSubmit = useCallback(
    (row: Row<Schema<T, P>>) => {
      const rowId = row.original.identifier;
      const formValues = getValues();
      const values = formValues ? formValues[rowId] : undefined;

      if (!values) return;

      const keys = Object.keys(values);

      onSubmit(() => {
        const data: RowData<P> = {} as RowData<P>;

        keys.forEach((key) => {
          const value = values[key];
          data[key as keyof P] = transformRowFieldData(key, value, dataSchema);
        });

        if (onSaveEditedRow) {
          onSaveEditedRow({
            ...data,
            id: {
              value: row.original.identifier,
              type: TABLE_CELL_TYPE.ID,
            },
          });
        }
      })();
    },
    [dataSchema, getValues, onSaveEditedRow, onSubmit]
  );

  const handleRowClick = useCallback(
    (row: Row<Schema<T, P>>) => {
      const isBulkSelectionActive = selectedRows.length > 0 && !hideCheckbox;

      if (isBulkSelectionActive) {
        const selectedId = row.original.id;
        const isSelected = selectedRows.includes(selectedId);

        handleRowSelect(selectedId, !isSelected);

        if (isSelected) {
          selectAllSetValue('selectAll', false, {
            shouldDirty: true,
          });
        }

        return;
      }

      onRowClick?.(row.original);
    },
    [handleRowSelect, hideCheckbox, onRowClick, selectAllSetValue, selectedRows]
  );

  return (
    <Wrapper>
      {rows.map((row) => {
        const isNew = isRowNew(row.original.identifier);
        const showSaveRowButton = showSaveButton(row.original.identifier);
        const showCancelEditRowButton = showCancelEditButton(row.original.identifier);

        return (
          <Card key={row.id} isEditing={isNew} onClick={!isNew ? () => handleRowClick(row) : undefined}>
            {!isNew && (!hideCheckbox || shouldShowRowControls) && (
              <CardHeader onClick={(e) => e.stopPropagation()}>
                {!hideCheckbox && (
                  <Checkbox
                    key={row.original.identifier}
                    checked={selectedRows.includes(row.original.id)}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      const selectedId = row.original.id;

                      handleRowSelect(selectedId, isChecked);

                      if (!isChecked) {
                        selectAllSetValue('selectAll', false, {
                          shouldDirty: true,
                        });
                      }
                    }}
                  />
                )}
                {shouldShowRowControls && (
                  <CardHeaderActions>
                    <TableRowControls noTableCell row={row.original} sx={{ p: 0 }} />
                  </CardHeaderActions>
                )}
              </CardHeader>
            )}

            <CardContent isNew={isNew}>
              {row.getVisibleCells().map((cell) => {
                const columnName = cell.column.columnDef.header?.toString() ?? '';
                const headerText = shortenText(columnName, 20);

                const value = cell.getValue() as string | undefined;
                const cellData = cell.getValue() as { uiDefinition?: P };
                const uiDefinition = cellData?.uiDefinition ?? (cell.column.columnDef.meta as ColumnMeta<P>)?.uiDefinition;

                const boxStyles = getUiDefinitionContainerStyles(uiDefinition);
                const textStyles = getUiDefinitionTextStyles(uiDefinition);

                const modifiedCell = {
                  ...cell,
                  column: { ...cell.column, id: cell.column.id },
                };

                const editMode = isCellEditable(row.original.identifier, modifiedCell.column.id);
                const cellKey = getFormFieldName(row, modifiedCell.column.id);

                const isActionCell = cell.column.id === BUTTONS_GROUP_ATTRIBUTE_KEY;

                const renderCellContent = () => {
                  const cellRenderer = cell.column.columnDef.cell;

                  if (typeof cellRenderer === 'function') {
                    return flexRender(cellRenderer, cell.getContext());
                  }

                  if (!value) return null;

                  return value;
                };

                return isNew ? (
                  <Box key={cellKey}>
                    {!isActionCell && <EditLabelTypography variant="subtitle2">{headerText}</EditLabelTypography>}
                    {!editMode ? (
                      renderCellContent()
                    ) : (
                      <TableCellEdit
                        agendaId={agendaId}
                        cell={cell}
                        row={row}
                        key={cellKey}
                        control={control}
                        dataSchema={dataSchema}
                        isNew={isNew}
                      />
                    )}
                  </Box>
                ) : (
                  <CardRow key={cell.id}>
                    {!!headerText && <CellLabel>{headerText}</CellLabel>}
                    <CellValueWrapper
                      uiDefinition={!!uiDefinition}
                      sx={{
                        ...boxStyles,
                        ...textStyles,
                      }}>
                      <TextContainer>{renderCellContent()}</TextContainer>
                    </CellValueWrapper>
                  </CardRow>
                );
              })}
            </CardContent>

            {hasEditableActions && (
              <EditButtons>
                {showCancelEditRowButton && (
                  <Button sx={{ background: 'white' }} variant="outlined" onClick={() => onCancelEditRow?.(row.original.identifier)}>
                    {t('common.cancel')}
                  </Button>
                )}
                {showSaveRowButton && (
                  <Button variant="contained" onClick={() => handleSubmit(row)}>
                    {t('common.save')}
                  </Button>
                )}
              </EditButtons>
            )}
          </Card>
        );
      })}
    </Wrapper>
  );
};

export const MobileTable = memo(MobileFlowTableBase) as typeof MobileFlowTableBase;
